import Drawer from '@/components/Drawer';
import { useSubmitOnEnter } from '@/components/Form/useSubmitOnEnter';
import { EMAIL_CENTER_ACTIONS } from '@/constants/actions';
import { readPaginator } from '@/models/emailCenter';
import * as EmailCenterSelectors from '@/selectors/emailCenterSelectors';
import * as JourneySelectors from '@/selectors/journeySelectors';
import {
  fetchEmailTemplatesRequest,
  linkJourneyEmailTemplateRequest,
  updateJourneyEmailTemplateRequest,
} from '@/services/api/emailCenter';
import {
  EmailTemplateModel,
  JourneyEmailTemplateLinkModel,
  LaravelPaginator,
} from '@/typings/api/emailCenter';
import { applyServerErrorsToForm, normalizeApiError } from '@/utils/apiErrors';
import { isJourneySpecificType } from '@/utils/emailCenter';
import { F, getIntlTranslation, INTL_ENTITY } from '@/utils/intl';
import { processAPIResponseNow } from '@/utils/request';
import { Alert, Button, Form, Select, Space, Switch, Tooltip } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useIntl, useSelector } from 'umi';
import {
  BCC_MAX,
  REPLY_TO_MAX,
  TEMPLATE_POOL_PAGE_SIZE,
} from '../../constants';

import { useEmailCenterValidateMessages } from '../../useValidateMessages';
import styles from './JourneyEmailsTab.less';

const emailListValidator = (
  max: number,
  invalidMessage: (email: string) => string,
  maxMessage: string,
) => ({
  validator: (_: unknown, value: string[] = []) => {
    if (value.length > max) return Promise.reject(maxMessage);
    const invalid = value.find(
      (entry) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(entry),
    );
    return invalid
      ? Promise.reject(invalidMessage(invalid))
      : Promise.resolve();
  },
});

const LinkTemplateDrawer: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const validateMessages = useEmailCenterValidateMessages();
  const [form] = Form.useForm();

  // The partner's own list, not the product's full one — see
  // `selectTranslationLanguages`. Unset means every language.
  const languageOptions = useSelector(
    EmailCenterSelectors.selectTranslationLanguageOptions,
  );

  const drawer = useSelector(EmailCenterSelectors.selectDrawer('link'));
  const templateTypes = useSelector(EmailCenterSelectors.selectTemplateTypes);
  const existingLinks = useSelector(
    EmailCenterSelectors.selectJourneyTemplates,
  );
  const journeyHashId = useSelector(
    EmailCenterSelectors.selectSelectedJourneyHashId,
  );
  const journeys = useSelector(JourneySelectors.selectJourneys);
  const isDefaultJourney = useMemo(
    () =>
      journeys.some(
        (journey) => journey.hashId === journeyHashId && journey.is_default,
      ),
    [journeys, journeyHashId],
  );

  const [saving, setSaving] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [pickerTemplates, setPickerTemplates] = useState<EmailTemplateModel[]>(
    [],
  );
  const [pickerTruncated, setPickerTruncated] = useState(false);

  const record = drawer.record as JourneyEmailTemplateLinkModel | null;
  const isEdit = drawer.mode === 'edit' && record != null;

  // The templates list has no search param — fetch one max-size page for the
  // picker and flag truncation beyond that.
  useEffect(() => {
    if (!drawer.visible) return;
    setGeneralError(null);
    fetchEmailTemplatesRequest({
      query: { type: 'transactional', page_size: TEMPLATE_POOL_PAGE_SIZE },
    }).then((result) =>
      processAPIResponseNow(
        result,
        {
          onSuccess: (paginator: LaravelPaginator<EmailTemplateModel>) => {
            const { list, total } = readPaginator(paginator);
            setPickerTemplates(list);
            setPickerTruncated(total > list.length);
          },
        },
        'data',
      ),
    );

    if (isEdit && record) {
      form.setFieldsValue({
        email_type: record.email_type,
        template_id: record.template?.id,
        language: record.language || undefined,
        bcc: record.bcc || [],
        reply_to: record.reply_to || [],
        is_enabled: record.is_enabled,
      });
    } else {
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawer.visible, record?.id]);

  /**
   * Creating: only event types that are not linked here yet — and, away from the
   * default journey, only the ones that are configured per lead source at all.
   *
   * The calendar event mails exist once for the account and always send from the
   * default journey (`journey_specific: false`), so offering them on a lead source
   * would create a second copy that nothing ever sends. An existing link stays
   * editable either way: hiding its own type would empty the field on open.
   */
  const eventTypeOptions = useMemo(() => {
    const linkedTypes = new Set(existingLinks.map((link) => link.email_type));
    return templateTypes
      .filter((templateType) => isEdit || !linkedTypes.has(templateType.key))
      .filter(
        (templateType) =>
          isEdit ||
          isDefaultJourney ||
          isJourneySpecificType(templateTypes, templateType.key),
      )
      .map((templateType) => ({
        value: templateType.key,
        label: templateType.label,
        description: templateType.description,
      }));
  }, [templateTypes, existingLinks, isEdit, isDefaultJourney]);

  const close = useCallback(() => {
    dispatch({
      type: EMAIL_CENTER_ACTIONS.CLOSE_DRAWER,
      payload: { drawer: 'link' },
    });
  }, [dispatch]);

  const handleSave = useCallback(async () => {
    if (!journeyHashId) return;
    const values = await form.validateFields();

    setSaving(true);
    setGeneralError(null);

    const result = isEdit
      ? await updateJourneyEmailTemplateRequest({
          journeyHashId,
          emailType: record!.email_type,
          payload: {
            language: values.language,
            bcc: values.bcc,
            reply_to: values.reply_to,
            is_enabled: values.is_enabled,
          },
        })
      : await linkJourneyEmailTemplateRequest({
          journeyHashId,
          payload: {
            template_id: values.template_id,
            email_type: values.email_type,
            language: values.language,
            bcc: values.bcc,
            reply_to: values.reply_to,
            is_enabled: values.is_enabled ?? true,
          },
        });
    setSaving(false);

    processAPIResponseNow(result, {
      showSuccessMessage: true,
      successEntity: INTL_ENTITY.EMAIL_TEMPLATE,
      onSuccess: () => {
        close();
        dispatch({
          type: EMAIL_CENTER_ACTIONS.FETCH_JOURNEY_TEMPLATES,
          payload: { journeyHashId },
        });
      },
      onError: (data) => {
        const normalized = normalizeApiError(data);
        applyServerErrorsToForm(form, normalized.fieldErrors, {
          onUnmapped: (key, messages) =>
            setGeneralError((prev) => prev ?? `${key}: ${messages.join(' ')}`),
        });
        if (Object.keys(normalized.fieldErrors).length === 0) {
          setGeneralError(normalized.message);
        }
      },
    });
  }, [journeyHashId, form, isEdit, record, close, dispatch]);

  const submitOnEnter = useSubmitOnEnter(handleSave, { enabled: !saving });

  return (
    <Drawer
      width={520}
      open={drawer.visible}
      onOpenChange={close}
      title={
        <F
          id={
            isEdit
              ? 'pages.emailCenter.journeys.links.editTitle'
              : 'pages.emailCenter.journeys.links.createTitle'
          }
          defaultMessage={
            isEdit ? 'Edit template link' : 'Link template to event'
          }
        />
      }
      footer={
        <Space className={styles.drawerFooter}>
          <Button onClick={close}>
            <F id="common.actions.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            <F id="common.actions.save" defaultMessage="Save" />
          </Button>
        </Space>
      }
    >
      {generalError && (
        <Alert
          className={styles.generalError}
          type="error"
          showIcon
          message={generalError}
        />
      )}

      <Form
        {...submitOnEnter}
        validateMessages={validateMessages}
        form={form}
        layout="vertical"
        initialValues={{ is_enabled: true }}
      >
        <Form.Item
          name="email_type"
          label={
            <F
              id="pages.emailCenter.templates.eventType"
              defaultMessage="Event"
            />
          }
          rules={[{ required: true }]}
        >
          <Select
            options={eventTypeOptions}
            disabled={isEdit}
            optionRender={(option) => (
              <Tooltip title={option.data.description} placement="left">
                <div>{option.label}</div>
              </Tooltip>
            )}
          />
        </Form.Item>

        <Form.Item
          name="template_id"
          label={<F entity={INTL_ENTITY.EMAIL_TEMPLATE} />}
          rules={[{ required: !isEdit }]}
          tooltip={
            isEdit ? (
              <F
                id="pages.emailCenter.journeys.links.templateLocked"
                defaultMessage="To use a different template, unlink this event and link it again."
              />
            ) : undefined
          }
        >
          <Select
            disabled={isEdit}
            showSearch
            optionFilterProp="label"
            options={pickerTemplates.map((template) => ({
              value: template.id,
              label: template.name,
            }))}
          />
        </Form.Item>
        {pickerTruncated && (
          <Alert
            className={styles.generalError}
            type="warning"
            showIcon
            message={
              <F
                id="pages.emailCenter.journeys.links.pickerTruncated"
                defaultMessage="Only the first {count} templates are listed."
                values={{ count: TEMPLATE_POOL_PAGE_SIZE }}
              />
            }
          />
        )}

        <Form.Item
          name="language"
          label={
            <F
              id="pages.emailCenter.journeys.links.language"
              defaultMessage="Language"
            />
          }
        >
          <Select allowClear options={languageOptions} />
        </Form.Item>

        <Form.Item
          name="bcc"
          label={
            <F id="pages.emailCenter.journeys.links.bcc" defaultMessage="BCC" />
          }
          rules={[
            emailListValidator(
              BCC_MAX,
              (email) =>
                getIntlTranslation(intl, {
                  id: 'pages.emailCenter.journeys.sender.replyToInvalid',
                  defaultMessage: '"{email}" is not a valid email address.',
                  values: { email },
                }),
              getIntlTranslation(intl, {
                id: 'pages.emailCenter.journeys.links.bccMax',
                defaultMessage: 'At most {max} BCC addresses.',
                values: { max: BCC_MAX },
              }),
            ),
          ]}
        >
          <Select
            mode="tags"
            open={false}
            suffixIcon={null}
            tokenSeparators={[',', ' ']}
          />
        </Form.Item>

        <Form.Item
          name="reply_to"
          label={
            <F
              id="pages.emailCenter.journeys.sender.replyTo"
              defaultMessage="Reply-to"
            />
          }
          rules={[
            emailListValidator(
              REPLY_TO_MAX,
              (email) =>
                getIntlTranslation(intl, {
                  id: 'pages.emailCenter.journeys.sender.replyToInvalid',
                  defaultMessage: '"{email}" is not a valid email address.',
                  values: { email },
                }),
              getIntlTranslation(intl, {
                id: 'pages.emailCenter.journeys.sender.replyToMax',
                defaultMessage: 'At most {max} reply-to addresses.',
                values: { max: REPLY_TO_MAX },
              }),
            ),
          ]}
        >
          <Select
            mode="tags"
            open={false}
            suffixIcon={null}
            tokenSeparators={[',', ' ']}
          />
        </Form.Item>

        <Form.Item
          name="is_enabled"
          label={
            <F
              id="pages.emailCenter.journeys.links.enabled"
              defaultMessage="Enabled"
            />
          }
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default LinkTemplateDrawer;
